import * as React from 'react';
import { Box, FormControl, Select, MenuItem, InputLabel } from '@mui/material';
import { MoneyTextField } from './MoneyInput';
import { MoneyTextFieldProps } from './types';

type CrDrType = 'CR' | 'DR';

type Props = Omit<MoneyTextFieldProps, 'value' | 'onChange'> & {
  value: number | null;
  onChange: (value: number | null) => void;
  currencySymbol?: string;
  locale?: string;
  /** Position of CR/DR dropdown - 'left' or 'right' */
  crDrPosition?: 'left' | 'right';
  /** If true, CR = negative, DR = positive. If false, CR = positive, DR = negative */
  swapLogic?: boolean;
  /** Whether to show CR/DR dropdown */
  showCrDr?: boolean;
  /** Default CR/DR selection */
  defaultCrDr?: CrDrType;
};

const isEmptyVal = (val: unknown) => val == null || val === '' || val === 0;

export const MoneyTextFieldWithCrDr: React.FC<Props> = ({
  value,
  onChange,
  currencySymbol = '₹',
  locale = 'en-IN',
  crDrPosition = 'right',
  swapLogic = false,
  showCrDr = true,
  defaultCrDr = 'CR',
  ...rest
}) => {
  const [crDr, setCrDr] = React.useState<CrDrType>(defaultCrDr);

  // Convert absolute value to signed value based on CR/DR and swapLogic
  const getSignedValue = React.useCallback((absValue: number | null, crDrType: CrDrType): number | null => {
    if (absValue == null) return null;
    
    const isPositive = swapLogic ? crDrType === 'DR' : crDrType === 'CR';
    return isPositive ? Math.abs(absValue) : -Math.abs(absValue);
  }, [swapLogic]);

  // Convert signed value to absolute value for display
  const getAbsoluteValue = React.useCallback((signedValue: number | null): number | null => {
    if (signedValue == null) return null;
    return Math.abs(signedValue);
  }, []);

  // Get CR/DR type from signed value
  const getCrDrFromValue = React.useCallback((signedValue: number | null): CrDrType => {
    if (signedValue == null) return defaultCrDr;
    
    const isPositive = signedValue >= 0;
    return swapLogic ? (isPositive ? 'DR' : 'CR') : (isPositive ? 'CR' : 'DR');
  }, [swapLogic, defaultCrDr]);

  // Initialize CR/DR from initial value
  React.useEffect(() => {
    if (value != null) {
      const initialCrDr = getCrDrFromValue(value);
      setCrDr(initialCrDr);
    }
  }, []);

  const handleValueChange = React.useCallback((newAbsValue: number | null) => {
    const signedValue = getSignedValue(newAbsValue, crDr);
    onChange(signedValue);
  }, [crDr, getSignedValue, onChange]);

  const handleCrDrChange = React.useCallback((newCrDr: CrDrType) => {
    setCrDr(newCrDr);
    
    // Convert current absolute value to new signed value
    const currentAbsValue = getAbsoluteValue(value);
    const newSignedValue = getSignedValue(currentAbsValue, newCrDr);
    onChange(newSignedValue);
  }, [value, getAbsoluteValue, getSignedValue, onChange]);

  const currentAbsValue = getAbsoluteValue(value);

  const inputComponent = (
    <MoneyTextField
      {...rest}
      value={currentAbsValue}
      onChange={handleValueChange}
      currencySymbol={currencySymbol}
      locale={locale}
      fullWidth={false}
    />
  );

  const dropdownComponent = showCrDr ? (
    <FormControl size="small" sx={{ minWidth: 60 }}>
      <Select
        value={crDr}
        onChange={(e) => handleCrDrChange(e.target.value as CrDrType)}
        sx={{
          '& .MuiSelect-select': {
            padding: '8px 12px',
            fontSize: '0.875rem',
          },
        }}
      >
        <MenuItem value="CR">CR</MenuItem>
        <MenuItem value="DR">DR</MenuItem>
      </Select>
    </FormControl>
  ) : null;

  return (
    <Box display="flex" alignItems="center" gap={1}>
      {crDrPosition === 'left' && dropdownComponent}
      {inputComponent}
      {crDrPosition === 'right' && dropdownComponent}
    </Box>
  );
};

